import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styles from './Hand.scss';
import { __, redirect } from '../../../../core/utils';
import { lowerCase, isEmpty } from 'lodash';
import classNames from 'classnames';
import { toNumber } from 'lodash';
import EditIcon from '../../../../assets/images/edit-16x16.svg';
import Tooltip from '../../../Tooltip';
import Image from '../../../Image';

export default function InfoBox({ infoBox, plan, onEdit, sourceType }) {
  const [showAbove, setShowAbove] = useState(false);
  if (!infoBox) return null;

  let pixelToMmRatio = 7.874; // 1 mm = 7.874 px

  let scale2 = toNumber(plan.scale2);
  let scale1 = toNumber(plan.scale1);

  let planScale = scale2 / scale1;

  function getInfoDataForObject(obj) {
    const type = obj.objectType;
    const width = obj.get('width');
    const height = obj.get('height');

    if (type === 'line') {
      const dx = obj.get('x2') - obj.get('x1');
      const dy = obj.get('y2') - obj.get('y1');
      const distance = Math.sqrt(dx * dx + dy * dy);
      return [['Length', distance, 'm']];
    }

    if (type === 'polygon') {
      let extent = 0;
      const points = obj.get('points');
      let oldx, oldy;
      const pts = [];

      for (let i = 0; i < points.length; i++) {
        pts.push(points[i].x, points[i].y);
        if (oldx !== undefined && oldy !== undefined) {
          extent += Math.sqrt(
            Math.pow(points[i].x - oldx, 2) + Math.pow(points[i].y - oldy, 2)
          );
        }
        oldx = points[i].x;
        oldy = points[i].y;
      }

      extent += Math.sqrt(
        Math.pow(points[0].x - oldx, 2) + Math.pow(points[0].y - oldy, 2)
      );
      pts.push(points[0].x, points[0].y);

      const area = areaFromCoords(pts);
      return [
        ['Width', width, 'm'],
        ['Height', height, 'm'],
        ['Area', area, 'm²'],
        ['Extent', extent, 'm'],
      ];
    }

    if (type === 'cloud') {
      const area = width * height;
      const extent = 2 * (width + height);

      return [
        ['Width', width, 'm'],
        ['Height', height, 'm'],
        ['Area', area, 'm²'],
        ['Extent', extent, 'm'],
      ];
    }

    if (type === 'ellipse' || type === 'rectangle') {
      const rx = width / 2;
      const ry = height / 2;

      const area = Math.PI * rx * ry;

      const extent =
        Math.PI * (3 * (rx + ry) - Math.sqrt((rx + 3 * ry) * (3 * rx + ry)));

      return [
        ['Width', width, 'm'],
        ['Height', height, 'm'],
        ['Area', area, 'm²'],
        ['Extent', extent, 'm'],
      ];
    }

    if (type === 'freehand' || type === 'marker') {
      const path = obj.get('path');
      const points = path.map((p) => ({
        x: p[p.length - 2],
        y: p[p.length - 1],
      }));
      let extent = 0;
      for (let i = 1; i < points.length; i++) {
        extent += Math.sqrt(
          Math.pow(points[i].x - points[i - 1].x, 2) +
            Math.pow(points[i].y - points[i - 1].y, 2)
        );
      }
      return [['Length', extent, 'm']];
    }

    return [];

    return [['Unknown', 0, '']];
  }

  function areaFromCoords(coords) {
    let area = 0;
    for (let i = 0; i < coords.length - 2; i += 2) {
      area += coords[i] * coords[i + 3] - coords[i + 2] * coords[i + 1];
    }
    return Math.abs(area / 2);
  }

  function convertToMeters(value, kind) {
    if (kind != 'Area') {
      return ((value / pixelToMmRatio) * planScale) / 1000; // in meters
    } else {
      let d = Math.sqrt(value);
      d = ((d / pixelToMmRatio) * planScale) / 1000;
      return d * d; // in meters
    }
  }

  const infoBoxRef = useRef(null);

  useEffect(() => {
    if (infoBoxRef.current) {
      const boxHeight = infoBoxRef.current.offsetHeight;
      const windowHeight = window.innerHeight;

      const willFitBelow = infoBox.position.top + boxHeight + 10 < windowHeight;

      setShowAbove(!willFitBelow);
    }
  }, [infoBox.position]);

  if (infoBox.object.objectType == 'plan') {
    const { title, code, preview_url, url_redirect, plan_id } =
      infoBox.object?.plan;

    return ReactDOM.createPortal(
      <div
        ref={infoBoxRef}
        className={classNames(styles.info, { [styles.above]: showAbove })}
        style={{
          top: showAbove
            ? infoBox.position.top -
              (infoBoxRef.current?.offsetHeight || 0) -
              35
            : infoBox.position.top + 5,
          left: infoBox.position.left,
        }}
      >
        <div className={styles.header} onClick={() => onEdit(plan_id)}>
          <EditIcon />
        </div>

        <div className={styles.image}>
          <Image src={preview_url} />
        </div>

        <div className={styles.footer}>
          <div className={styles.planInfo}>
            <span>{code}</span>
            <span>{title}</span>
          </div>

          <Tooltip text={__('hand.info-box.go-to')}>
            <div
              className={styles.goToButton}
              onClick={() => {
                redirect(url_redirect);
              }}
            >
              <span>{__('hand.info-box.go-to')}</span>
            </div>
          </Tooltip>
        </div>
      </div>,
      document.body
    );
  }

  const values =
    sourceType != 'pdf' ? getInfoDataForObject(infoBox.object) : [];

  return ReactDOM.createPortal(
    <div
      ref={infoBoxRef}
      className={classNames(styles.info, { [styles.above]: showAbove })}
      style={{
        top: showAbove
          ? infoBox.position.top - (infoBoxRef.current?.offsetHeight || 0) - 35
          : infoBox.position.top + 5,
        left: infoBox.position.left,
      }}
    >
      {values.map(([label, value, unit], index) => (
        <p key={index}>
          <strong>
            {__('canvas.toolbar.hand.infobox.' + lowerCase(label))}:
          </strong>{' '}
          {convertToMeters(value, label).toFixed(2)} {unit}
        </p>
      ))}

      {infoBox.object.userFullName && (
        <p>
          <strong>{__('canvas.toolbar.hand.infobox.created_by')}:</strong>{' '}
          {infoBox.object.userFullName}
        </p>
      )}
    </div>,
    document.body
  );
}

InfoBox.propTypes = {
  infoBox: PropTypes.shape({
    object: PropTypes.object.isRequired,
    position: PropTypes.shape({
      top: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
    }),
  }),
  onClose: PropTypes.func.isRequired,
};
